import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';
import { getImageUrl } from '../../utils/helpers';
import { Button } from '../ui';
import {
  LayoutDashboard,
  Users,
  UserCircle,
  FileText,
  PenLine,
  Calendar,
  Folder,
  Settings,
  X,
  Building2,
  BarChart3,
  Package,
  ClipboardList,
  Image as ImageIcon,
  Sparkles,
  Ticket,
  ArrowRight,
  ArrowLeft,
  Search,
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose, isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, isClient, isSuperAdmin } = useAuth();
  const { isRTL, t } = useLanguage();
  const { agency } = useWhiteLabel();

  // Helper: check if user has access to item
  const hasAccess = (item) => {
    const userRole = user?.role;
    if (!userRole) return false;
    return item.roles.includes(userRole) || (userRole === 'admin' && item.roles.includes('agency_admin')) || (userRole === 'team_manager' && item.roles.includes('team_manager'));
  };

  // Navigation groups - Option 2: أبسط
  const navigationGroups = isSuperAdmin
    ? [
        { labelKey: null, items: [{ nameKey: 'dashboard', href: '/super-admin/dashboard', icon: LayoutDashboard, roles: ['super_admin'] }] },
        { labelKey: 'management', items: [
          { nameKey: 'agencies', href: '/super-admin/agencies', icon: Building2, roles: ['super_admin'] },
          { nameKey: 'plans', href: '/super-admin/plans', icon: Folder, roles: ['super_admin'] },
          { nameKey: 'features', href: '/super-admin/features', icon: Sparkles, roles: ['super_admin'] },
        ]},
        { labelKey: 'reports', items: [{ nameKey: 'reports', href: '/super-admin/reports', icon: BarChart3, roles: ['super_admin'] }] },
        { labelKey: 'support', items: [{ nameKey: 'tickets', href: '/super-admin/tickets', icon: Ticket, roles: ['super_admin'] }] },
        { labelKey: null, items: [{ nameKey: 'settings', href: '/super-admin/settings', icon: Settings, roles: ['super_admin'] }] },
      ]
    : [
        { labelKey: null, items: [{ nameKey: 'dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'agency_admin', 'team_manager', 'designer', 'writer', 'client', 'seo_specialist'] }] },
        { labelKey: 'contentAndPosts', items: [
          { nameKey: 'posts', href: '/posts', icon: FileText, roles: ['admin', 'agency_admin', 'team_manager', 'designer', 'writer', 'client', 'seo_specialist'] },
          { nameKey: 'scheduledPosts', href: '/posts/scheduled', icon: Calendar, roles: ['admin', 'agency_admin', 'team_manager', 'designer', 'writer', 'seo_specialist'] },
          { nameKey: 'designRequests', href: '/design-requests', icon: ClipboardList, roles: ['admin', 'agency_admin', 'team_manager', 'client'] },
          { nameKey: 'editor', href: '/articles', icon: PenLine, roles: ['admin', 'agency_admin', 'team_manager', 'writer', 'seo_specialist'] },
        ]},
        { labelKey: 'teamAndClients', items: [
          { nameKey: 'team', href: '/users', icon: UserCircle, roles: ['admin', 'agency_admin', 'team_manager'] },
          { nameKey: 'clients', href: '/clients', icon: Users, roles: ['admin', 'agency_admin', 'team_manager', 'designer', 'writer', 'seo_specialist'] },
          { nameKey: 'packages', href: '/packages', icon: Package, roles: ['admin', 'agency_admin', 'team_manager'] },
        ]},
        { labelKey: 'mediaLibrary', items: [{ nameKey: 'mediaLibrary', href: '/media', icon: ImageIcon, roles: ['admin', 'agency_admin', 'team_manager', 'designer'] }] },
        { labelKey: 'seoSection', items: [{ nameKey: 'seoPlans', href: '/seo', icon: Search, roles: ['admin', 'agency_admin', 'team_manager', 'seo_specialist'] }] },
        { labelKey: 'support', items: [{ nameKey: 'tickets', href: '/tickets', icon: Ticket, roles: ['admin', 'agency_admin', 'team_manager'] }] },
        { labelKey: null, items: [{ nameKey: 'settings', href: '/settings', icon: Settings, roles: ['admin', 'agency_admin', 'team_manager'] }] },
      ];

  // Filter groups: only show groups that have at least one visible item
  const filteredGroups = navigationGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(hasAccess),
    }))
    .filter((group) => group.items.length > 0);

  // Listen for agency updates to refresh WhiteLabelContext
  useEffect(() => {
    const handleAgencyUpdate = () => {
      // WhiteLabelContext will handle the refresh
      window.dispatchEvent(new CustomEvent('refreshWhiteLabel'));
    };
    window.addEventListener('agencyUpdated', handleAgencyUpdate);

    return () => {
      window.removeEventListener('agencyUpdated', handleAgencyUpdate);
    };
  }, []);

  const isActive = (path) => {
    if (path === '/dashboard' || path === '/super-admin/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/super-admin/dashboard';
    }
    // /posts/scheduled: active only when on scheduled page
    if (path === '/posts/scheduled') {
      return location.pathname.startsWith('/posts/scheduled');
    }
    // /posts: active on /posts but NOT when on /posts/scheduled
    if (path === '/posts') {
      return location.pathname.startsWith('/posts') && !location.pathname.startsWith('/posts/scheduled');
    }
    return location.pathname.startsWith(path);
  };

  // Calculate trial days remaining
  const getTrialDaysRemaining = () => {
    if (!agency || agency.subscription_status !== 'trial' || !agency.subscription_end_date) {
      return null;
    }
    const endDate = new Date(agency.subscription_end_date);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const trialDaysRemaining = getTrialDaysRemaining();
  const isTrialActive = agency && agency.subscription_status === 'trial' && trialDaysRemaining !== null && trialDaysRemaining > 0;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-600 bg-opacity-75 z-40 lg:hidden transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-16 z-20 h-[calc(100vh-4rem)] transition-all duration-300 ease-in-out
          bg-white/95 backdrop-blur-sm
          border-slate-200/80
          shadow-xl shadow-slate-200/50
          ${isRTL ? 'right-0 border-l' : 'left-0 border-r'}
          ${isOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'}
          lg:translate-x-0 lg:shadow-none
          ${isCollapsed ? 'w-16 lg:w-16' : 'w-64 lg:w-64'}
          flex flex-col
        `}
      >
        {/* Close button (mobile only) */}
        <button
          onClick={onClose}
          className={`lg:hidden absolute top-4 z-50 p-2 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all duration-200 ${
            isRTL ? 'left-4' : 'right-4'
          }`}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Collapse toggle (desktop only) */}
        <div
          className={`
            hidden lg:flex items-center justify-between px-3 pt-4 pb-1
          `}
        >
          {/* Logo / agency (اختياري مستقبلاً) */}
          <div className="flex-1 min-w-0">
            {!isCollapsed && agency?.logo_url && (
              <img
                src={getImageUrl(agency.logo_url)}
                alt={agency.name}
                className="h-8 w-auto max-w-full object-contain"
              />
            )}
          </div>
          <button
            type="button"
            onClick={onToggleCollapse}
            className="ml-2 inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors"
            title={
              isCollapsed
                ? isRTL
                  ? 'توسيع القائمة'
                  : 'Expand sidebar'
                : isRTL
                ? 'تصغير القائمة'
                : 'Collapse sidebar'
            }
          >
            {isCollapsed ? (
              isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />
            ) : isRTL ? (
              <ArrowRight className="w-4 h-4" />
            ) : (
              <ArrowLeft className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 px-3 pb-4 pt-3 overflow-y-auto scrollbar-thin">
          <nav className="space-y-6">
            {filteredGroups.map((group, groupIdx) => (
              <div key={group.labelKey || groupIdx} className="space-y-1.5">
                {group.labelKey && !isCollapsed && (
                  <p className={`px-3 py-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-widest ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t(group.labelKey)}
                  </p>
                )}
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);

                    return (
                      <Link
                        key={item.nameKey || item.href}
                        to={item.href}
                        onClick={onClose}
                        className={`
                          flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-xl text-sm font-medium
                          transition-all duration-200 ease-out
                          ${active
                            ? 'bg-gradient-to-r from-sky-500 to-emerald-500 text-white shadow-md shadow-sky-500/25'
                            : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900 active:scale-[0.98]'
                          }
                        `}
                      >
                        <span className={`
                          flex items-center justify-center w-8 h-8 rounded-lg shrink-0
                          ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}
                        `}>
                          <Icon className="w-4 h-4" />
                        </span>
                        {!isCollapsed && (
                          <span className="truncate">{t(item.nameKey)}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User info (mobile only) */}
          <div className="lg:hidden mt-8 pt-6 border-t border-slate-200/80">
            <div className="px-3 py-3 rounded-xl bg-slate-50/80">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate mt-0.5">{user?.email}</p>
              <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-200/80 text-slate-600 capitalize">
                {user?.role}
              </span>
            </div>
          </div>
        </div>

        {/* Trial Info - Fixed at bottom */}
        {isTrialActive && isAdmin && !isSuperAdmin && (
          <div className="px-3 pb-4 pt-4 mx-3 mb-2 rounded-2xl border border-amber-200/60 bg-gradient-to-br from-amber-50 to-orange-50/80">
            <div className={`space-y-3 ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="flex items-center gap-3">
                <span className="flex w-9 h-9 items-center justify-center rounded-xl bg-amber-100 text-amber-600 shrink-0">
                  <Sparkles className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-900">
                    {isRTL ? 'تجربة مجانية' : 'Free Trial'}
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    {isRTL 
                      ? `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'يوم متبقي' : 'أيام متبقية'}`
                      : `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} left`
                    }
                  </p>
                  {trialDaysRemaining <= 3 && (
                    <p className="text-xs text-amber-700 font-semibold mt-0.5">
                      {isRTL ? '⚠️ تنتهي قريباً!' : '⚠️ Ending soon!'}
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={() => {
                  navigate('/payment');
                  onClose();
                }}
                variant="primary"
                size="sm"
                className="w-full rounded-xl bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-md shadow-sky-500/25 text-xs font-semibold py-2.5"
              >
                {isRTL ? 'اشتراك الآن' : 'Subscribe Now'}
                {isRTL ? <ArrowLeft className="w-3.5 h-3.5 ms-2" /> : <ArrowRight className="w-3.5 h-3.5 ms-2" />}
              </Button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;


